import { useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { regulationLabel } from '@/utils/regulationLabels'
import { LiveAppearanceControls, LivePrepackCard } from './PrepackLiveCopy'
import { templateShowsJerseyNumbers } from './prepackCardModel'
import PrepackJerseyNumbers from './PrepackJerseyNumbers'
import { useCardAspect, useHeaderAspect } from './useCardAspect'

/**
 * The builder's live preview: the card for the previewed regulation, the
 * regulation switch, the re-check indicator and failure warning, and the
 * appearance controls. The card stays up through re-checks and leg edits
 * (see usePrepackQuoteTargets). `previewPack` has no title or tagline: the
 * Live* components add them from `copyStore`, so typing doesn't re-render this.
 * `jerseyLegs` are the legs with a jersey or a shirt number when the template
 * shows jerseys (null otherwise), for the Jerseys box.
 */
export default function PrepackLivePreview({
  quote,
  copyStore,
  previewPack,
  fallbackAppearancePack,
  defaultTitle,
  templates,
  selectedTemplate,
  orgSlug,
  targetRegulations,
  previewRegulation,
  onPreviewRegulationChange,
  availableRegulations,
  controlsDisabled,
  onTemplateVarsChange,
  onPaintingChange,
  jerseyLegs,
  jerseyNumbersNeedResave,
  onLegChoiceChange,
}) {
  // The live card's shape and its header picture's, which the banner picture's framing box takes for the whole-card
  // and the header fill. Measured around the card and the controls: the card comes first, and comes and goes with the preview.
  const cardsRef = useRef(null)
  const cardAspect = useCardAspect(cardsRef)
  const headerAspect = useHeaderAspect(cardsRef)
  const { quoteTargetsQuery, recheckingQuote, recheckFailed, updatingPreview, previewVerdict } = quote
  return (
    <aside aria-label="Live prepack preview" className="w-full shrink-0 bg-[var(--prepacks-surface)]/40 lg:w-[420px] lg:overflow-y-auto">
      <header className="flex items-center justify-between border-b border-[var(--prepacks-border)] px-5 py-3.5">
        <div className="flex items-center gap-2">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--prepacks-muted)]">Live preview</p>
          {recheckingQuote ? (
            // Not a live region: it would be announced every 30 s.
            <span className="flex items-center gap-1 text-[11px] leading-4 text-[var(--prepacks-muted)]">
              <Loader2 aria-hidden="true" className="h-3 w-3 animate-spin" />
              Re-checking odds…
            </span>
          ) : null}
        </div>
        {targetRegulations.length > 1 ? (
          <select
            aria-label="Preview regulation"
            value={previewRegulation}
            disabled={controlsDisabled}
            onChange={(event) => onPreviewRegulationChange(event.target.value)}
            className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-2 py-1 text-[11px] text-[var(--wf-color-text)] disabled:opacity-50"
          >
            {targetRegulations.map((regulation) => (
              <option key={regulation} value={regulation}>
                {regulationLabel(regulation, availableRegulations)}
              </option>
            ))}
          </select>
        ) : null}
      </header>
      <div ref={cardsRef} className="flex flex-col items-center gap-4 p-5">
        {recheckFailed && previewPack ? (
          <p role="status" className="w-full rounded border border-[var(--wf-color-warning-border)] bg-[var(--wf-color-warning-soft)] px-3 py-2 text-xs text-[var(--wf-color-warning)]">
            Couldn’t re-check odds. Prices shown are from{' '}
            {new Date(quoteTargetsQuery.dataUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
          </p>
        ) : null}
        {previewPack ? (
          <div className="w-full" aria-busy={updatingPreview || undefined}>
            <LivePrepackCard store={copyStore} pack={previewPack} defaultTitle={defaultTitle} showActions={false} templates={templates} />
          </div>
        ) : (
          <p role="status" className="w-full rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-4 text-center text-xs text-[var(--prepacks-muted)]">
            {previewVerdict === 'invalid'
              ? 'Preview unavailable for this rejected regulation.'
              : previewVerdict === 'unavailable'
              ? 'Preview unavailable for this regulation.'
              : 'Preview will appear after the selected regulation is checked.'}
          </p>
        )}

        <LiveAppearanceControls
          store={copyStore}
          orgSlug={orgSlug}
          template={selectedTemplate}
          pack={previewPack}
          fallbackPack={fallbackAppearancePack}
          defaultTitle={defaultTitle}
          disabled={controlsDisabled}
          cardAspect={cardAspect}
          headerAspect={headerAspect}
          onChange={onTemplateVarsChange}
          onPaintingChange={onPaintingChange}
        />
        {jerseyLegs ? <PrepackJerseyNumbers legs={jerseyLegs} numbers={templateShowsJerseyNumbers(selectedTemplate)} jerseyNumbersNeedResave={jerseyNumbersNeedResave} disabled={controlsDisabled} onChange={onLegChoiceChange} /> : null}
      </div>
    </aside>
  )
}
